import { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, Mail, CheckCircle2, MapPin, Store, CalendarPlus, Sparkles, Tag, Pencil } from "lucide-react";
import { motion } from "framer-motion";
import { useData } from '@happyspace/sdk/data';
import { useManagedCollection } from "../admin/useManagedCollection";
import { AdminItem } from "../admin/AdminControls";
import AdminFieldModal, { type FieldDef } from "../admin/AdminFieldModal";
import { SHOW_GET_UPDATES_CARD } from "../config/siteFlags";

// "Get Updates" newsletter card — admin-manageable (hide/unhide/edit) from the
// Admin console, without disturbing any other section of the hero.
const GET_UPDATES_DEFAULTS = [
  { key: "get_updates", heading: "Get Updates", subtext: "Events • Deals • New Businesses" },
];
const GET_UPDATES_FIELDS: FieldDef[] = [
  { key: "heading", label: "Heading", type: "text", required: true },
  { key: "subtext", label: "Subtext", type: "text" },
];

const heroQuickLinks = [
  { label: "Add Business", to: "/add-business", icon: Store },
  { label: "Add Event", to: "/add-event", icon: CalendarPlus },
  { label: "Experiences", to: "/experiences", icon: Sparkles },
  { label: "Deals", to: "/deals", icon: Tag },
];

interface HeroProps {
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  onSearch: (overrideQuery?: string) => void;
}

export default function Hero({
  searchQuery,
  setSearchQuery,
  onSearch,
}: HeroProps) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [subError, setSubError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { insert: subscribeEmail } = useData("newsletter_subscribers");

  // Admin-manageable "Get Updates" card (hide / unhide / edit heading+subtext).
  const getUpdatesMc = useManagedCollection<{ key: string; heading: string; subtext: string }>(
    "hero_get_updates",
    GET_UPDATES_DEFAULTS,
    (d) => d.key
  );
  const getUpdatesItem = getUpdatesMc.items[0];

  async function handleSubscribe() {
    const trimmed = email.trim();
    if (!trimmed) { setSubError("Please enter an email address."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) { setSubError("Please enter a valid email."); return; }
    setSubError(null);
    setSubmitting(true);
    try {
      await subscribeEmail({ email: trimmed, source: "hero-subscribe", subscribedAt: new Date().toISOString() });
      setSubscribed(true);
      setEmail("");
    } catch (err) {
      setSubError(err instanceof Error ? err.message : "Couldn't subscribe. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section
      className="relative w-full"
      style={{ minHeight: "160px" }}
    >
      <div className="absolute inset-0 overflow-hidden">
        <img
          src="https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782403552147-fm1v6c.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/45" />
        <svg viewBox="0 0 1440 24" preserveAspectRatio="none" style={{ position: "absolute", left: 0, bottom: "-1px", width: "100%", height: "18px", display: "block" }}>
          <path d="M0,0 Q720,24 1440,0 L1440,24 L0,24 Z" fill="#ffffff" />
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 pt-3 sm:pt-4 pb-8 flex flex-col lg:flex-row items-start justify-between gap-4 lg:gap-6">
        {/* LEFT / MAIN content area */}
        <div className="flex-1 flex flex-col items-center text-center w-full min-w-0">
          {/* Heading */}
          <motion.h1
            initial={typeof window === 'undefined' ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-white text-[22px] sm:text-[25px] md:text-[27px] leading-tight tracking-tight drop-shadow-sm"
            style={{ fontFamily: "'Lora', serif", textShadow: "0 1px 3px rgba(0,0,0,0.4)" }}
          >
            <span className="font-normal">Connecting </span>
            <span className="font-extrabold">South Asian's</span>
            <span className="font-normal"> in </span>
            <span className="font-extrabold">Dallas–DFW</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={typeof window === 'undefined' ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-1 text-white text-[13px] sm:text-[15px] md:text-[16px] font-semibold"
            style={{ fontFamily: "'Lora', serif", textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}
          >
            <span className="font-normal">Discover </span>
            <span className="text-[#fbbf24] font-bold">
              Businesses, Events, Reviews & Stories
            </span>
          </motion.p>

          {/* Quick action links */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="mt-3 flex flex-nowrap items-center justify-center gap-1.5 sm:gap-2 w-full"
          >
            {heroQuickLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="bg-[#f59e0b] hover:bg-[#d97f06] text-white text-[9.5px] xs:text-[10.5px] sm:text-[12px] font-semibold px-1.5 sm:px-3.5 py-1.5 sm:py-2 min-h-[32px] sm:min-h-[36px] rounded-md transition-colors duration-200 shadow-sm flex items-center justify-center text-center leading-tight flex-1 sm:flex-initial whitespace-nowrap"
              >
                {link.label}
              </Link>
            ))}
          </motion.div>
        </div>

        {/* RIGHT — Get Updates card (admin-manageable: hide / unhide / edit) */}
        {SHOW_GET_UPDATES_CARD && getUpdatesItem && (!getUpdatesItem._hidden || getUpdatesMc.adminMode) && (
          <AdminItem mc={getUpdatesMc} item={getUpdatesItem} fields={GET_UPDATES_FIELDS} title="Get Updates card">
            <motion.div
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="hidden lg:flex flex-col bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3 w-full lg:w-[260px] shrink-0 self-start mt-1"
            >
              <h3 className="text-[14px] font-bold text-gray-800 mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {getUpdatesItem.heading}
              </h3>
              <p className="text-[11px] text-gray-500 mb-2">
                {getUpdatesItem.subtext}
              </p>
              {subscribed ? (
                <div className="flex items-center gap-2 text-[#51ba4f] text-[12px] font-semibold py-1">
                  <CheckCircle2 className="w-4 h-4" /> Subscribed!
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 flex items-center bg-gray-50 rounded-md px-2.5 py-1.5 border border-gray-200 min-w-0">
                      <Mail className="w-3.5 h-3.5 text-gray-400 mr-1.5 shrink-0" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setSubError(null); }}
                        onKeyDown={(e) => { if (e.key === "Enter") handleSubscribe(); }}
                        placeholder="Your Email"
                        className="w-full text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-transparent min-w-0"
                      />
                    </div>
                    <button
                      onClick={handleSubscribe}
                      disabled={submitting}
                      className="bg-[#51ba4f] hover:bg-[#43a041] disabled:bg-gray-300 text-white text-[12px] font-bold px-3 py-1.5 rounded-md transition-colors duration-200 shrink-0"
                    >
                      {submitting ? "..." : "Subscribe"}
                    </button>
                  </div>
                  {subError && (
                    <p className="text-red-500 text-[10px] mt-1.5">{subError}</p>
                  )}
                </>
              )}
            </motion.div>
          </AdminItem>
        )}
      </div>

      {/* Bottom inverted crescent — shallow white arc cutting into hero */}
      <svg
        className="hero-curve"
        viewBox="0 0 1440 24"
        preserveAspectRatio="none"
        style={{
          position: "absolute",
          left: 0,
          bottom: "-1px",
          width: "100%",
          height: "18px",
          display: "block",
          zIndex: 3,
        }}
      >
        <path
          d="M0,0 Q720,24 1440,0 L1440,24 L0,24 Z"
          fill="#ffffff"
        />
      </svg>
    </section>
  );
}
